import React, { useEffect } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Button,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { format } from "date-fns";
import useAdminReturns from "../../hooks/useAdminReturns";

// Import form components
import ReturnReviewForm from "../../components/admin/returns/ReturnReviewForm";
import MarkItemsReceivedForm from "../../components/admin/returns/MarkItemsReceivedForm";
import ProcessRefundForm from "../../components/admin/returns/ProcessRefundForm";

// Helper to get chip color based on return status (can be shared)
const getReturnStatusChipColor = (status) => {
  switch (status?.toLowerCase()) {
    case "requested":
      return "warning";
    case "under_review":
    case "shipped_by_user":
      return "info";
    case "approved":
    case "partially_approved":
      return "secondary";
    case "received":
      return "primary";
    case "completed":
      return "success";
    case "rejected":
      return "error";
    default:
      return "default";
  }
};

/**
 * Admin Return Detail Page component
 * Displays detailed information about a return and provides actions based on status
 * @returns {JSX.Element} AdminReturnDetailPage component
 */
const AdminReturnDetailPage = () => {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("returns"); // Namespace for returns
  const { returnId } = useParams();

  const getIdFromUrl = () => {
    const urlParts = window.location.pathname.split("/");
    return urlParts[urlParts.length - 1];
  };

  const id = returnId || getIdFromUrl();

  const { currentReturn, status, error, loadReturnDetail } = useAdminReturns();

  useEffect(() => {
    if (!id) {
      console.error("Return ID is undefined. Check route parameters.");
      return;
    }
    const cleanup = loadReturnDetail(id);
    return () => {
      cleanup();
    };
  }, [id, loadReturnDetail]); // Added loadReturnDetail to dependencies

  if (!id) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{t("admin.detail.errors.invalidId")}</Alert>
        <Box mt={2}>
          <Button
            component={RouterLink}
            to="/admin/returns"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            {t("admin.detail.backToReturns")}
          </Button>
        </Box>
      </Container>
    );
  }

  if (status === "loading" && !currentReturn) {
    // Show loading only if no data yet
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: "center" }}>
        <Typography variant="h6" gutterBottom>
          {t("admin.detail.loading")}
        </Typography>
        <CircularProgress />
      </Container>
    );
  }

  if (status === "failed" && !currentReturn) {
    // Show error if loading failed and no data
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error || tCommon("status.error")}</Alert>
        <Box mt={2}>
          <Button
            component={RouterLink}
            to="/admin/returns"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            {t("admin.detail.backToReturns")}
          </Button>
        </Box>
      </Container>
    );
  }

  if (!currentReturn) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info">
          {t("admin.detail.errors.dataNotAvailable")}
          {status === "succeeded"
            ? t("admin.detail.errors.dataMissing")
            : t("admin.detail.errors.pleaseWait")}
        </Alert>
        <Box mt={2}>
          <Button
            component={RouterLink}
            to="/admin/returns"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            {t("admin.detail.backToReturns")}
          </Button>
        </Box>
      </Container>
    );
  }

  const returnDetail = currentReturn;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          component={RouterLink}
          to="/admin/returns"
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          sx={{ mr: 2 }}
        >
          {t("admin.detail.backToReturns")}
        </Button>
        <Typography variant="h4" component="h1">
          {t("admin.detail.title")}
        </Typography>
        <Chip
          label={t(
            `shared.status.${returnDetail.status?.toLowerCase()}`,
            returnDetail.status || tCommon("status.notApplicable")
          )}
          color={getReturnStatusChipColor(returnDetail.status)}
          sx={{ ml: 2 }}
        />
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              {t("admin.detail.customer.title")}
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary={t("admin.detail.customer.company")}
                  secondary={
                    returnDetail.user?.companyName ||
                    tCommon("status.notApplicable")
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t("admin.detail.customer.email")}
                  secondary={
                    returnDetail.user?.email || tCommon("status.notApplicable")
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t("admin.detail.customer.userId")}
                  secondary={
                    returnDetail.userId || tCommon("status.notApplicable")
                  }
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              {t("admin.detail.order.title")}
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary={t("admin.detail.order.orderNumber")}
                  secondary={
                    <RouterLink to={`/admin/orders/${returnDetail.orderId}`}>
                      {returnDetail.order?.orderNumber ||
                        `${returnDetail.orderId?.substring(0, 8)}...` ||
                        tCommon("status.notApplicable")}
                    </RouterLink>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t("admin.detail.order.returnCreated")}
                  secondary={
                    returnDetail.createdAt
                      ? format(new Date(returnDetail.createdAt), "PPpp")
                      : tCommon("status.notApplicable")
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t("admin.detail.order.returnReason")}
                  secondary={
                    returnDetail.reason || t("admin.detail.order.noReason")
                  }
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t("admin.detail.items.title")}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("admin.detail.items.product")}</TableCell>
                    <TableCell align="center">
                      {t("admin.detail.items.quantity")}
                    </TableCell>
                    <TableCell>{t("admin.detail.items.reason")}</TableCell>
                    <TableCell>{t("admin.detail.items.condition")}</TableCell>
                    <TableCell>{t("admin.detail.items.status")}</TableCell>
                    <TableCell>
                      {t("admin.detail.items.adminDecision")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {returnDetail.items && returnDetail.items.length > 0 ? (
                    returnDetail.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          {item.orderItem?.product?.name ||
                            tCommon("status.notApplicable")}
                        </TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell>
                          {item.reason || tCommon("status.notApplicable")}
                        </TableCell>
                        <TableCell>
                          {item.condition || tCommon("status.notApplicable")}
                        </TableCell>
                        <TableCell>
                          {t(
                            `shared.status.${item.status?.toLowerCase()}`,
                            item.status || tCommon("status.notApplicable")
                          )}
                        </TableCell>
                        <TableCell>
                          {item.adminDecision
                            ? `${t(
                                `shared.status.${item.adminDecision?.toLowerCase()}`,
                                item.adminDecision
                              )} (${
                                item.adminQuantity === null ||
                                item.adminQuantity === undefined
                                  ? "-"
                                  : item.adminQuantity
                              })`
                            : t("admin.detail.items.pending")}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Alert severity="info">
                          {t("admin.detail.items.noItems")}
                        </Alert>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t("admin.detail.actions.title")}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {returnDetail.status === "requested" && (
              <Box>
                <Typography variant="subtitle1" color="secondary" gutterBottom>
                  {t("admin.detail.actions.reviewReturn")}
                </Typography>
                <ReturnReviewForm returnDetail={returnDetail} />
              </Box>
            )}
            {returnDetail.status === "shipped_by_user" && (
              <Box mt={3}>
                <Typography variant="subtitle1" color="secondary" gutterBottom>
                  {t("admin.detail.actions.markReceived")}
                </Typography>
                <MarkItemsReceivedForm returnDetail={returnDetail} />
              </Box>
            )}
            {(returnDetail.status === "approved" ||
              returnDetail.status === "partially_approved") && (
              <Alert severity="info" sx={{ mt: 3 }}>
                {t("admin.detail.actions.waitingForShipment")}
              </Alert>
            )}
            {returnDetail.status === "received" && (
              <Box mt={3}>
                <Typography variant="subtitle1" color="secondary" gutterBottom>
                  {t("admin.detail.actions.processRefund")}
                </Typography>
                <ProcessRefundForm returnDetail={returnDetail} />
              </Box>
            )}
            {(returnDetail.status === "completed" ||
              returnDetail.status === "rejected") && (
              <Alert severity="info">
                {t("admin.detail.actions.noActionsAvailable", {
                  status: t(
                    `shared.status.${returnDetail.status?.toLowerCase()}`,
                    returnDetail.status
                  ),
                })}
              </Alert>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t("admin.detail.notes.title")}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">
                  {t("admin.detail.notes.customerNotes")}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {returnDetail.notes ||
                    t("admin.detail.notes.noCustomerNotes")}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">
                  {t("admin.detail.notes.adminNotes")}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {returnDetail.adminNotes ||
                    t("admin.detail.notes.noAdminNotes")}
                </Typography>
              </Grid>
            </Grid>

            {returnDetail.refundMethod && (
              <Box mt={2}>
                <Typography variant="subtitle2">
                  {t("admin.detail.refund.title")}
                </Typography>
                <Typography variant="body2">
                  {t("admin.detail.refund.method")}:{" "}
                  {t(
                    `admin.detail.forms.processRefund.methods.${returnDetail.refundMethod}`,
                    returnDetail.refundMethod
                  )}
                </Typography>
                <Typography variant="body2">
                  {t("admin.detail.refund.amount")}:{" "}
                  {returnDetail.refundAmount
                    ? `${parseFloat(returnDetail.refundAmount).toFixed(2)} CHF`
                    : tCommon("status.notApplicable")}
                </Typography>
                <Typography variant="body2">
                  {t("admin.detail.refund.processed")}:{" "}
                  {returnDetail.refundedAt
                    ? format(new Date(returnDetail.refundedAt), "PPpp")
                    : tCommon("status.notApplicable")}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AdminReturnDetailPage;
